import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { IAiModelEntity } from '@common/microservice-types/db/ai-models/ai-model';
import { IAiModelFamilyEntity } from '@common/microservice-types/db/ai-models/family';
import { environment } from '@env';
import { logger } from '@services/logger/logger.service';

@Injectable({ providedIn: 'root' })
export class AiModelsService
{
    private readonly models$ = new BehaviorSubject<IAiModelEntity[]>([]);
    private readonly families$ = new BehaviorSubject<IAiModelFamilyEntity[]>([]);
    private readonly modelsByFeature$ = new BehaviorSubject<Record<string, IAiModelEntity[]>>({});
    private allModels: IAiModelEntity[] = [];
    private defaultChatModel = '';
    private defaultModelsByFeature: Record<string, string> = {};

    constructor(
        private http: HttpClient,
        private translateService: TranslateService,
    )
    {
        // Model copy is localized server-side, so re-fetch when the language
        // changes (only once models have been loaded post-auth).
        this.translateService.onLangChange.subscribe(({ lang }) =>
        {
            if (this.allModels.length > 0)
            {
                this.loadModels(lang);
            }
        });
    }

    /**
     * Loads models from the shared backend (GET /api/r2-chat/models), passing the
     * current locale so the backend returns localized model copy. Requires an
     * authenticated MS session, so it is called after login (not in the
     * constructor). Safe to call repeatedly — it just re-fetches.
     */
    public async loadModels(locale = this.translateService.currentLang || 'en'): Promise<void>
    {
        try
        {
            const models = await firstValueFrom(
                this.http.get<IAiModelEntity[]>(
                    `${ environment.office_api_host }/api/r2-chat/models`,
                    { params: { locale } },
                ),
            ) ?? [];
            this.allModels = models;

            this.defaultModelsByFeature = {
                'CHAT': this.resolveDefaultApiName(models, 'chat', 'CHAT'),
                'TEXT_TO_IMAGE': this.resolveDefaultApiName(models, 'textToImage', 'TEXT_TO_IMAGE'),
                'SPEECH_TO_TEXT': this.resolveDefaultApiName(models, 'speechToText', 'SPEECH_TO_TEXT'),
            };
            this.defaultChatModel = this.defaultModelsByFeature[ 'CHAT' ];

            this.families$.next(this.collectFamilies(models));

            const choosable = models.filter(m => m.isChoosableOnUI && !m.isRetired);
            const byFeature: Record<string, IAiModelEntity[]> = {};

            for (const model of choosable)
            {
                for (const feature of model.features ?? [])
                {
                    byFeature[ feature ] = [ ...(byFeature[ feature ] ?? []), model ];
                }
            }

            this.modelsByFeature$.next(byFeature);
            this.models$.next(byFeature[ 'CHAT' ] ?? []);
        }
        catch (e)
        {
            logger.error('AiModelsService', 'loadModels', e);
        }
    }

    /** Resolves the default model apiName for a feature, falling back to the first model exposing it. */
    private resolveDefaultApiName(models: IAiModelEntity[], defaultFeatureName: string, feature: string): string
    {
        return models.find(m => m.defaultForFeatures?.some(f => f.name === defaultFeatureName))?.apiName
            ?? models.find(m => m.features?.includes(feature))?.apiName
            ?? '';
    }

    private collectFamilies(models: IAiModelEntity[]): IAiModelFamilyEntity[]
    {
        const familyMap = new Map<number, IAiModelFamilyEntity>();
        for (const m of models)
        {
            const family = m.family;
            if (family?.name && typeof family.id === 'number' && !familyMap.has(family.id))
            {
                familyMap.set(family.id, family);
            }
        }
        return Array.from(familyMap.values()).sort((a, b) =>
        {
            const orderDifference = (a.order ?? 0) - (b.order ?? 0);
            return orderDifference || a.name.localeCompare(b.name);
        });
    }

    public getModels$(): Observable<IAiModelEntity[]>
    {
        return this.models$.asObservable();
    }

    public getModels(): IAiModelEntity[]
    {
        return this.models$.getValue();
    }

    public getModelByApiName(apiName: string): IAiModelEntity | null
    {
        return this.models$.getValue().find(m => m.apiName === apiName) ?? null;
    }

    public getModelById(id?: number | null): IAiModelEntity | null
    {
        if (id == null) { return null; }
        return this.allModels.find(m => m.id === id) ?? null;
    }

    public getDefaultChatModel(): string
    {
        return this.defaultChatModel;
    }

    public getDefaultChatModelId(): number | null
    {
        return this.getModelByApiName(this.defaultChatModel)?.id ?? null;
    }

    /**
     * apiName of the feature's default model. Unlike {@link getDefaultModelForFeature}
     * this does not need the model to be UI-choosable — the speech-to-text default
     * never appears in the picker, so only its apiName is available.
     */
    public getDefaultApiNameForFeature(feature: string): string
    {
        return this.defaultModelsByFeature[ feature ] ?? '';
    }

    public getDefaultModelForFeature(feature: string): IAiModelEntity | null
    {
        const defaultApiName = this.defaultModelsByFeature[ feature ];
        if (!defaultApiName) { return null; }
        const models = this.modelsByFeature$.getValue()[ feature ] ?? [];
        return models.find(m => m.apiName === defaultApiName)
            ?? this.allModels.find(m => m.apiName === defaultApiName)
            ?? models[ 0 ]
            ?? null;
    }

    public getFamilies$(): Observable<IAiModelFamilyEntity[]>
    {
        return this.families$.asObservable();
    }

    public getChatFamilies(): IAiModelFamilyEntity[]
    {
        return this.collectFamilies(this.models$.getValue());
    }

    public sortModelsByProvider(models: IAiModelEntity[]): IAiModelEntity[]
    {
        const providerOrder = new Map(this.collectFamilies(models).map((family, index) => [ family.id, index ]));
        return [ ...models ].sort((a, b) =>
        {
            const providerDifference = (providerOrder.get(a.family?.id ?? a.familyId ?? -1) ?? Number.MAX_SAFE_INTEGER)
                - (providerOrder.get(b.family?.id ?? b.familyId ?? -1) ?? Number.MAX_SAFE_INTEGER);
            return providerDifference || (a.order ?? 0) - (b.order ?? 0);
        });
    }

    public getModelsByFeature$(): Observable<Record<string, IAiModelEntity[]>>
    {
        return this.modelsByFeature$.asObservable();
    }

    public getModelsByFeature(): Record<string, IAiModelEntity[]>
    {
        return this.modelsByFeature$.getValue();
    }

    public getFamilyIconUri(family: string, theme: 'WHITE' | 'DARK'): string | null
    {
        const f = this.families$.getValue().find(fam => fam.name === family);
        const asset = f?.assets?.find(a => a.theme === theme || !a.theme);
        return asset?.image.uri ?? f?.assets?.find(a => a.theme === 'WHITE')?.image.uri ?? null;
    }

    public findCheaperModel(currentApiName: string, tokensLeft: number, maxOutputBytes: number): IAiModelEntity | null
    {
        const models = this.models$.getValue();
        const current = models.find(m => m.apiName === currentApiName) ?? null;

        const computeHyperTokens = (m: IAiModelEntity) =>
            Math.ceil(maxOutputBytes * m.limits.maxOutputHyperTokens / m.limits.maxOutputBytes);

        const sortAscByHyperTokens = (list: IAiModelEntity[]) =>
            [ ...list ].sort((a, b) => computeHyperTokens(a) - computeHyperTokens(b));

        const candidates = models.filter(m => m.apiName !== currentApiName);
        const viable = candidates.filter(m => computeHyperTokens(m) <= tokensLeft);

        if (viable.length === 0)
        {
            return null;
        }

        if (current)
        {
            const sameFamily = viable.filter(m => m.family?.name === current.family?.name);
            if (sameFamily.length > 0)
            {
                return sortAscByHyperTokens(sameFamily)[ 0 ];
            }
        }

        return sortAscByHyperTokens(viable)[ 0 ];
    }
}
